
import React from 'react';
import { ShieldCheck, Search, BarChart3, Paintbrush, Hammer, MessageCircle } from 'lucide-react';

const Services: React.FC = () => {
  return (
    <div className="min-h-screen relative">
      {/* Background Image with Blur */}
      <div 
        className="fixed inset-0 bg-fixed bg-cover bg-center z-0"
        style={{ backgroundImage: "url('https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80')" }}
      >
        <div className="absolute inset-0 bg-white/40 backdrop-blur-[10px]"></div>
      </div>
      
      <div className="relative z-10">
        {/* Hero */}
        <section className="bg-navy/90 py-16 text-center text-white relative overflow-hidden backdrop-blur-sm">
            <div className="absolute top-0 left-0 w-32 h-32 bg-orange rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
            <div className="absolute top-0 right-0 w-32 h-32 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
            
            <div className="container mx-auto px-4 relative z-10">
                <h1 className="text-4xl md:text-5xl font-bold mb-4">Nos Services</h1>
                <p className="text-xl text-gray-300 max-w-2xl mx-auto">Des solutions personnalisées pour tous vos besoins immobiliers au Bénin.</p>
            </div>
        </section>

        <div className="container mx-auto px-4 py-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">

              {/* Service 1: Solutions Personnalisées */}
              <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-8 shadow-sm hover:shadow-xl transition duration-300 border border-white/50 flex flex-col">
                  <div className="flex items-start mb-6">
                      <div className="bg-blue-50 p-4 rounded-2xl text-navy mr-4">
                          <ShieldCheck size={32} />
                      </div>
                      <div>
                          <h2 className="text-2xl font-bold text-navy mb-2">Solutions Personnalisées</h2>
                          <p className="text-gray-600">Pour garantir votre sérénité.</p>
                      </div>
                  </div>
                  
                  <div className="space-y-6 flex-grow">
                      <div className="bg-gray-50 p-4 rounded-xl">
                          <h3 className="font-bold text-navy mb-1">🛡️ Locataire Protégé</h3>
                          <p className="text-sm text-gray-600 mb-3">Accompagnement complet, garanties et protection juridique.</p>
                          <a href="https://wa.me/22997406767?text=Je veux en savoir plus sur le service Locataire Protégé" target="_blank" rel="noreferrer" className="text-sm font-bold text-green-600 hover:underline flex items-center">
                              En savoir plus <MessageCircle size={14} className="ml-1"/>
                          </a>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-xl">
                          <h3 className="font-bold text-navy mb-1">🏠 Propriétaire Protégé</h3>
                          <p className="text-sm text-gray-600 mb-3">Sécurisez vos biens et optimisez leur rentabilité.</p>
                          <a href="https://wa.me/22997406767?text=Je veux en savoir plus sur le service Propriétaire Protégé" target="_blank" rel="noreferrer" className="text-sm font-bold text-green-600 hover:underline flex items-center">
                              En savoir plus <MessageCircle size={14} className="ml-1"/>
                          </a>
                      </div>
                  </div>
              </div>

              {/* Service 2: Prospection Ciblée (Featured) */}
              <div className="bg-navy/95 backdrop-blur-sm text-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition duration-300 border border-navy/50 flex flex-col relative overflow-hidden">
                  <div className="absolute top-4 right-4 bg-orange text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">
                      Service Phare
                  </div>
                  
                  <div className="flex items-start mb-6">
                      <div className="bg-white/10 p-4 rounded-2xl text-orange mr-4">
                          <Search size={32} />
                      </div>
                      <div>
                          <h2 className="text-2xl font-bold text-white mb-2">Prospection Ciblée</h2>
                          <p className="text-gray-300">Ne cherchez plus, nous trouvons pour vous.</p>
                      </div>
                  </div>
                  
                  <p className="text-gray-300 mb-6 leading-relaxed flex-grow">
                      Nous recherchons activement le bien idéal pour vous. Que vous soyez acheteur, locataire, vendeur ou propriétaire, nous trouvons exactement ce qui vous correspond grâce à notre réseau étendu.
                  </p>

                  <a 
                      href="https://wa.me/22997406767?text=Je souhaite lancer une prospection ciblée" 
                      target="_blank" 
                      rel="noreferrer"
                      className="w-full bg-orange hover:bg-orange/90 text-white font-bold py-3 rounded-xl flex items-center justify-center transition"
                  >
                      <MessageCircle size={20} className="mr-2" />
                      Lancer une prospection
                  </a>
              </div>

              {/* Service 3: Gestion Locative */}
              <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-8 shadow-sm hover:shadow-xl transition duration-300 border border-white/50 flex flex-col">
                  <div className="flex items-start mb-6">
                      <div className="bg-green-50 p-4 rounded-2xl text-green-600 mr-4">
                          <BarChart3 size={32} />
                      </div>
                      <div>
                          <h2 className="text-2xl font-bold text-navy mb-2">Gestion Locative</h2>
                      </div>
                  </div>
                  <p className="text-gray-600 mb-6 flex-grow">
                      Confiez-nous la gestion complète : encaissement des loyers, contrats, entretien. Vous recevez votre argent, nous nous occupons du reste.
                  </p>
                  <a 
                      href="https://wa.me/22997406767?text=Je souhaite déléguer ma gestion locative" 
                      target="_blank" 
                      rel="noreferrer"
                      className="w-full bg-[#25D366] hover:bg-[#20bd5a] text-white font-bold py-3 rounded-xl flex items-center justify-center transition"
                  >
                      <MessageCircle size={20} className="mr-2" />
                      Déléguer ma gestion
                  </a>
              </div>

              {/* Service 4: Décoration */}
              <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-8 shadow-sm hover:shadow-xl transition duration-300 border border-white/50 flex flex-col">
                  <div className="flex items-start mb-6">
                      <div className="bg-purple-50 p-4 rounded-2xl text-purple-600 mr-4">
                          <Paintbrush size={32} />
                      </div>
                      <div>
                          <h2 className="text-2xl font-bold text-navy mb-2">Décoration Intérieure</h2>
                      </div>
                  </div>
                  <p className="text-gray-600 mb-6 flex-grow">
                       Transformez votre espace avec notre service de décoration personnalisée. Idéal pour nouvelle construction ou nouvel aménagement.
                  </p>
                  <a 
                      href="https://wa.me/22997406767?text=Je souhaite un devis pour de la décoration" 
                      target="_blank" 
                      rel="noreferrer"
                      className="w-full bg-[#25D366] hover:bg-[#20bd5a] text-white font-bold py-3 rounded-xl flex items-center justify-center transition"
                  >
                      <MessageCircle size={20} className="mr-2" />
                      Demander un devis
                  </a>
              </div>

              {/* Service 5: Construction */}
              <div className="md:col-span-2 bg-white/95 backdrop-blur-sm rounded-3xl p-8 shadow-sm hover:shadow-xl transition duration-300 border border-white/50 flex flex-col md:flex-row items-center gap-6">
                  <div className="bg-orange/10 p-6 rounded-3xl text-orange flex-shrink-0">
                      <Hammer size={48} />
                  </div>
                  <div className="flex-grow">
                      <h2 className="text-2xl font-bold text-navy mb-2">Accompagnement Projets de Construction</h2>
                      <p className="text-gray-600 mb-4">
                          De la conception à la réalisation, nous vous guidons : conseil architectural, mise en relation avec des professionnels, suivi de chantier.
                      </p>
                      <a 
                          href="https://wa.me/22997406767?text=Je veux démarrer un projet de construction" 
                          target="_blank" 
                          rel="noreferrer"
                          className="inline-flex bg-[#25D366] hover:bg-[#20bd5a] text-white font-bold py-3 px-6 rounded-xl items-center justify-center transition"
                      >
                          <MessageCircle size={20} className="mr-2" />
                          Démarrer un projet
                      </a>
                  </div>
              </div>

            </div>
        </div>
      </div>
    </div>
  );
};

export default Services;
